import React from 'react';
import { Dropdown, message } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { PolicyDetailResponse, PolicySummaryResponse } from '../utils/types';
import { useSelector } from 'react-redux';
import { intervalSelector, getUserProfile } from '@src/store/common/selectors';
import { formatDateTime } from '../utils/helper';
import moment from 'moment-timezone';

const exportPolicyDetailAsCSV = (
  data: PolicyDetailResponse,
  filename: string,
  profile: any,
  interval: any,
) => {
  if (!data) return;

  const { cdn_weight, policy_curve, policy_detail } = data;

  const summaryTitle = [
    'Policies Dashboard report generated by Conviva Pulse.',
    `This report must be used in conformance with customer's`,
    `Subscription Agreement & Data Policy with Conviva.`,
    `Report generated on ${moment().tz(profile.tzName).format('MMM DD YYYY HH:mm:ss Z')} (${profile.tzCode})`,
    `Copyright ${moment().year()} Conviva Inc.`,
    `Conviva Confidential`,
    ``,
    `Time range: ${moment(interval.from).format('YYYY-MM-DD HH:mm:ss Z')} to ${moment(interval.to).format('YYYY-MM-DD HH:mm:ss Z')}`,
    `Filters`,
    ``,
  ];

  const cdnSection = [
    'Request Assignment',
    'CDN,% Configured,% Actual',
    ...cdn_weight.map((item) => [item.cdn, item.weight / 100, item.actual_weight].join(',')),
    '',
  ];

  const curveSection = [
    '# Policy Curve',
    'Time,Requests',
    ...policy_curve.map(([date, value]) => [formatDateTime(date), value]),
    '',
  ];

  const detailSection = [
    'Policy Details',
    'ID,Rank,Last Updated,Filter,Filter Rule',
    ...policy_detail.map((d) =>
      [
        d.policy_id,
        d.index,
        d.updated_at,
        `"${d.filter}"`, // Quote in case of commas
        `"${d.filter_rule}"`,
      ].join(','),
    ),
  ];

  const csvContent = [...summaryTitle, ...detailSection, ...curveSection, ...cdnSection].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  link.click();
};

const exportFullSummaryAsCSV = (
  data: PolicySummaryResponse,
  filename: string,
  profile: any,
  interval: any,
) => {
  if (!data) return;

  const { Resource, Policy } = data;

  const summaryTitle = [
    'Policies Dashboard report generated by Conviva Pulse.',
    `This report must be used in conformance with customer's`,
    `Subscription Agreement & Data Policy with Conviva.`,
    `Report generated on ${moment().tz(profile.tzName).format('MMM DD YYYY HH:mm:ss Z')} (${profile.tzCode})`,
    `Copyright ${moment().year()} Conviva Inc.`,
    `Conviva Confidential`,
    ``,
    `Time range: ${moment(interval.from).format('YYYY-MM-DD HH:mm:ss Z')} to ${moment(interval.to).format('YYYY-MM-DD HH:mm:ss Z')}`,
    `Filters:`,
    ``,
  ];

  const cdnSection = [
    'Summary',
    'CDN,Requests per CND',
    ...Resource.total_resources.map((res) => `${res.cdn_name},${res.cdn_requests}`),
    '',
  ];

  const policySection = [
    'All Policies',
    'ID,Rank,Policy Name,Requests per policy,Last Updated',
    ...Policy.map(
      (v) => `${v.original_id},${v.index},${v.name},${v.requests_per_policy},${v.updated_at}`,
    ),
  ];

  const csvContent = [...summaryTitle, ...cdnSection, ...policySection].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  link.click();
};

interface KebabMenuProps {
  detailData: PolicyDetailResponse | PolicySummaryResponse | null; // adjust type accordingly
  policyId: number | null;
  profile: any;
}

const KebabMenu = (prop: KebabMenuProps) => {
  const isSummary = location.pathname.includes('summary');
  const { detailData, profile } = prop;
  const interval = useSelector(intervalSelector);
  const userProfile = useSelector(getUserProfile);

  const handleMenuClick = ({ key }) => {
    const file_name = `Policies_Dashboard_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`;
    if (key === 'export-summary') {
      exportFullSummaryAsCSV(detailData as PolicySummaryResponse, file_name, profile, interval);
    } else if (key === 'export-detail') {
      exportPolicyDetailAsCSV(detailData as PolicyDetailResponse, file_name, profile, interval);
    } else if (key.startsWith('share-')) {
      const start_day = interval.from.slice(0, 10);
      const end_day = interval.to.slice(0, 10);
      let next_url = '';
      let shareUrl = '';
      if (key === 'share-summary') {
        next_url = `/app/experience-insights/dashboard/policies-tracking/summary?start_day=${start_day}&end_day=${end_day}`;
      } else {
        next_url = `/app/experience-insights/dashboard/policies-tracking/detail?policy_id=${prop.policyId}&start_day=${start_day}&end_day=${end_day}`;
      }
      shareUrl = `${window.location.origin}/customer_switch/${userProfile.customer_id}/?next=${encodeURIComponent(next_url)}`;
      try {
        navigator.clipboard.writeText(shareUrl);
        message.info('Save to clipboard');
      } catch (err) {
        message.error('Failed to copy');
      }
    }
  };

  const menuItems = [
    ...(isSummary
      ? [{ key: 'export-summary', label: 'Export' }]
      : [{ key: 'export-detail', label: 'Export' }]),
    ...(isSummary
      ? [{ key: 'share-summary', label: 'Share' }]
      : [{ key: 'share-detail', label: 'Share' }]),
  ];

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: handleMenuClick }}
      trigger={['click']}
      placement="bottomRight"
    >
      <MoreOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
    </Dropdown>
  );
};

export default KebabMenu;
